import { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { BookOpen, FileQuestion, Upload, Trash2, Users, BarChart3, TrendingUp, CheckCircle, FileJson, FileText, Eye, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { MathJax } from 'better-react-mathjax';
import * as api from '../utils/api';

interface Course {
  id: string;
  name: string;
  description: string;
  teacherIds: string[];
}

interface Quiz {
  id: string;
  title: string;
  courseId: string;
  teacherId: string;
  description: string;
  timeLimit: number;
  questions: any[];
  createdAt: string;
}

export default function TeacherDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadQuizOpen, setUploadQuizOpen] = useState(false);

  const [quizJson, setQuizJson] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadMode, setUploadMode] = useState<'file' | 'text'>('file');
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [quizDetailOpen, setQuizDetailOpen] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [addCourseOpen, setAddCourseOpen] = useState(false);
  const [courseActionLoading, setCourseActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      if (!user) return;

      // Load data from API
      const [coursesData, quizzesData, allCoursesData] = await Promise.all([
        api.getCoursesByTeacher(user.id),
        api.getQuizzesByTeacher(user.id),
        api.getAllCourses(true)  // Pass true to get ALL courses for selection
      ]);

      setCourses(coursesData);
      setQuizzes(quizzesData);
      setAllCourses(allCoursesData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState<{
    quizStats: { name: string; attempts: number; avgScore: number }[];
    totalAttempts: number;
    completedQuizzes: number;
    uniqueStudents: number;
  }>({ quizStats: [], totalAttempts: 0, completedQuizzes: 0, uniqueStudents: 0 });
  
  const [studentsData, setStudentsData] = useState<{
    id: string;
    name: string;
    email: string;
    quizzesTaken: number;
    averageScore: number;
  }[]>([]);

  // Load students data
  useEffect(() => {
    const loadStudents = async () => {
      try {
        const students = await api.getTeacherStudents();
        setStudentsData(students);
        // Update unique students count in analytics
        setAnalyticsData(prev => ({
          ...prev,
          uniqueStudents: students.length
        }));
      } catch (error) {
        console.error('Error loading students:', error);
      }
    };
    
    if (user) {
      loadStudents();
    }
  }, [user]);

  // Load analytics when quizzes change
  useEffect(() => {
    const loadAnalytics = async () => {
      if (quizzes.length === 0) return;
      
      try {
        const analytics = await api.getTeacherAnalytics();
        
        // Load individual quiz analytics to get attempts and scores
        const quizStatsPromises = quizzes.map(async (quiz) => {
          try {
            const quizAnalytics = await api.getQuizAnalytics(quiz.id);
            return {
              name: quiz.title.length > 20 ? quiz.title.substring(0, 20) + '...' : quiz.title,
              attempts: quizAnalytics.totalAttempts,
              avgScore: quizAnalytics.averageScore
            };
          } catch {
            return {
              name: quiz.title.length > 20 ? quiz.title.substring(0, 20) + '...' : quiz.title,
              attempts: 0,
              avgScore: 0
            };
          }
        });
        
        const quizStats = await Promise.all(quizStatsPromises);
        
        setAnalyticsData(prev => ({
          quizStats,
          totalAttempts: analytics.totalSubmissions,
          completedQuizzes: quizzes.length,
          uniqueStudents: prev.uniqueStudents // Keep the value from students load
        }));
      } catch (error) {
        console.error('Error loading analytics:', error);
      }
    };
    
    loadAnalytics();
  }, [quizzes]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles(e.target.files);
    }
  };

  const handleUploadQuizFiles = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    if (uploadMode === 'file' && (!selectedFiles || selectedFiles.length === 0)) {
      toast.error('Please select at least one JSON file');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      if (uploadMode === 'file' && selectedFiles) {
        // Process multiple files
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          
          // Check file type
          if (!file.name.endsWith('.json')) {
            toast.error(`${file.name} is not a JSON file`);
            errorCount++;
            continue;
          }

          try {
            const fileContent = await file.text();
            const quizData = JSON.parse(fileContent);

            // Transform the uploaded JSON to match our Quiz structure
            const newQuiz = {
              title: quizData.title || quizData.quizName,
              courseId: quizData.courseId,
              teacherId: user.id,
              description: quizData.description || '',
              timeLimit: quizData.timeLimit || 30,
              questions: quizData.questions.map((q: any, index: number) => ({
                id: q.questionId || q.id || `q${index + 1}`,
                type: q.type || 'mcq',
                question: q.question || q.content,
                options: q.options || q.choices,
                correctAnswer: q.correctAnswer || '',
                points: q.points || 10
              }))
            };

            await api.createQuiz(newQuiz);
            successCount++;
          } catch (fileError: any) {
            console.error(`Error processing ${file.name}:`, fileError);
            errorCount++;
          }
        }
      } else if (uploadMode === 'text') {
        // Process pasted JSON
        const quizData = JSON.parse(quizJson);

        const newQuiz = {
          title: quizData.title || quizData.quizName,
          courseId: quizData.courseId,
          teacherId: user.id,
          description: quizData.description || '',
          timeLimit: quizData.timeLimit || 30,
          questions: quizData.questions.map((q: any, index: number) => ({
            id: q.questionId || q.id || `q${index + 1}`,
            type: q.type || 'mcq',
            question: q.question || q.content,
            options: q.options || q.choices,
            correctAnswer: q.correctAnswer || '',
            points: q.points || 10
          }))
        };

        await api.createQuiz(newQuiz);
        successCount = 1;
      }

      // Show results
      if (successCount > 0 && errorCount === 0) {
        toast.success(`${successCount} quiz${successCount > 1 ? 'zes' : ''} uploaded successfully!`);
      } else if (successCount > 0 && errorCount > 0) {
        toast.success(`${successCount} quiz${successCount > 1 ? 'zes' : ''} uploaded, ${errorCount} failed`);
      } else {
        toast.error('Failed to upload quizzes. Please check your JSON format.');
      }

      // Reset and close
      setUploadQuizOpen(false);
      setQuizJson('');
      setSelectedFiles(null);
      loadDashboardData();
    } catch (error: any) {
      console.error('Upload quiz error:', error);
      toast.error(error.message || 'Failed to upload quiz. Please check your JSON format.');
    }
  };

  const handleUploadQuiz = handleUploadQuizFiles;

  const handleDeleteQuiz = async (quizId: string) => {
    if (!confirm('Are you sure you want to delete this quiz?')) return;

    try {
      await api.deleteQuiz(quizId);
      toast.success('Quiz deleted successfully');
      loadDashboardData();
    } catch (error) {
      console.error('Delete quiz error:', error);
      toast.error('Failed to delete quiz');
    }
  };

  const handleAddCourse = async (courseId: string) => {
    if (!user) return;
    try {
      setCourseActionLoading(courseId);
      await api.addTeacherToCourse(courseId, user.id);
      toast.success('Course added successfully');
      loadDashboardData();
      setAddCourseOpen(false);
    } catch (error: any) {
      console.error('Add course error:', error);
      toast.error(error.message || 'Failed to add course');
    } finally {
      setCourseActionLoading(null);
    }
  };

  const handleRemoveCourse = async (courseId: string) => {
    if (!user) return;
    if (!confirm('Are you sure you want to remove this course from your list?')) return;
    try {
      setCourseActionLoading(courseId);
      await api.removeTeacherFromCourse(courseId, user.id);
      toast.success('Course removed successfully');
      loadDashboardData();
    } catch (error: any) {
      console.error('Remove course error:', error);
      toast.error(error.message || 'Failed to remove course');
    } finally {
      setCourseActionLoading(null);
    }
  };

  const sampleQuizJson = {
    title: "Unit 4-5 Probability & Statistics Quiz",
    courseId: courses[0]?.id || "course-1",
    description: "Comprehensive quiz covering probability distributions and statistical inference",
    timeLimit: 45,
    questions: [
      {
        id: "q1",
        type: "mcq",
        question: "What is $$P(A \\cup B)$$ if $$P(A) = 0.3$$, $$P(B) = 0.4$$, and $$P(A \\cap B) = 0.1$$?",
        options: ["$$0.2$$", "$$0.4$$", "$$0.6$$", "$$0.8$$"],
        correctAnswer: "$$0.6$$",
        points: 10
      },
      {
        id: "q2",
        type: "mcq",
        question: "Which of the following is true about the Central Limit Theorem?",
        options: [
          "It applies only to normal distributions",
          "Sample means approach normal distribution as sample size increases",
          "It requires a sample size of at least 100",
          "It only works with discrete variables"
        ],
        correctAnswer: "Sample means approach normal distribution as sample size increases",
        points: 10
      },
      {
        id: "q3",
        type: "short-answer",
        question: "Calculate the mean of the dataset: 2, 4, 6, 8, 10",
        correctAnswer: "$$6$$",
        points: 15
      }
    ]
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  // Determine which view to show based on the current route
  const currentPath = location.pathname;
  const showCoursesOnly = currentPath === '/teacher/courses';
  const showQuizzesOnly = currentPath === '/teacher/quizzes';
  const showStudentsOnly = currentPath === '/teacher/students';
  const showAnalyticsOnly = currentPath === '/teacher/analytics';
  const showDashboard = currentPath === '/teacher';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl text-gray-900">
          {showCoursesOnly ? 'My Courses' : 
           showQuizzesOnly ? 'Quiz Management' : 
           showStudentsOnly ? 'My Students' : 
           showAnalyticsOnly ? 'Analytics' : 
           'Teacher Dashboard'}
        </h2>
        <p className="text-gray-600 mt-1">
          {showCoursesOnly ? 'View and manage your courses' : 
           showQuizzesOnly ? 'Upload and manage your quizzes' : 
           showStudentsOnly ? 'View student progress and performance' : 
           showAnalyticsOnly ? 'Track quiz performance and student analytics' : 
           'Manage your courses and quizzes'}
        </p>
      </div>

      {/* Summary Cards - Show on Dashboard and Analytics pages */}
      {(showDashboard || showAnalyticsOnly) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm text-gray-600">Total Students</CardTitle>
              <Users className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-gray-900">{analyticsData.uniqueStudents}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm text-gray-600">Quiz Attempts</CardTitle>
              <CheckCircle className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-gray-900">{analyticsData.totalAttempts}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm text-gray-600">Active Quizzes</CardTitle>
              <FileQuestion className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl text-gray-900">{analyticsData.completedQuizzes}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Charts - Show on Dashboard and Analytics pages */}
      {(showDashboard || showAnalyticsOnly) && analyticsData.quizStats.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quiz Performance</CardTitle>
              <CardDescription>Average scores by quiz</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.quizStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="avgScore" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quiz Attempts</CardTitle>
              <CardDescription>Number of attempts per quiz</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.quizStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="attempts" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State for Analytics */}
      {showAnalyticsOnly && analyticsData.totalAttempts === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Analytics Data Yet</h3>
              <p className="text-gray-500 mb-4">Students haven't taken any quizzes yet</p>
              <Button onClick={() => navigate('/teacher/quizzes')}>
                <FileQuestion className="w-4 h-4 mr-2" />
                Manage Quizzes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Courses - Show on Dashboard and Courses pages */}
      {(showDashboard || showCoursesOnly) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>My Courses</CardTitle>
                <CardDescription>Courses assigned to you</CardDescription>
              </div>
              <Dialog open={addCourseOpen} onOpenChange={setAddCourseOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Course
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Course</DialogTitle>
                    <DialogDescription>
                      Select a course to add to your teaching list
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2 mt-4">
                    {allCourses
                      .filter(c => !c.teacherIds?.includes(user?.id || ''))
                      .map((course) => (
                        <div 
                          key={course.id} 
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                        >
                          <div>
                            <p className="font-medium">{course.name}</p>
                            <p className="text-sm text-gray-500">{course.description}</p>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => handleAddCourse(course.id)}
                            disabled={courseActionLoading === course.id}
                          >
                            {courseActionLoading === course.id ? 'Adding...' : 'Add'}
                          </Button>
                        </div>
                      ))}
                    {allCourses.filter(c => !c.teacherIds?.includes(user?.id || '')).length === 0 && (
                      <p className="text-center py-8 text-gray-500">
                        You are already assigned to all available courses
                      </p>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {courses.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No courses assigned yet. Click "Add Course" to get started.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.map((course) => (
                  <Card key={course.id} className="relative group">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleRemoveCourse(course.id)}
                      disabled={courseActionLoading === course.id}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                    <CardHeader>
                      <CardTitle className="text-lg">{course.name}</CardTitle>
                      <CardDescription>{course.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Students List - Show only on Students page */}
      {showStudentsOnly && (
        <Card>
          <CardHeader>
            <CardTitle>My Students</CardTitle>
            <CardDescription>Students who have taken your quizzes</CardDescription>
          </CardHeader>
          <CardContent>
            {studentsData.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Yet</h3>
                <p className="text-gray-500">No students have taken your quizzes yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Quizzes Taken</TableHead>
                    <TableHead>Average Score</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {studentsData.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{student.email}</TableCell>
                      <TableCell>{student.quizzesTaken}</TableCell>
                      <TableCell>{student.averageScore.toFixed(1)}%</TableCell>
                      <TableCell>
                        <Badge 
                          variant={student.averageScore >= 70 ? 'default' : 'secondary'}
                          className={student.averageScore >= 70 ? 'bg-green-600' : 'bg-orange-600'}
                        >
                          {student.averageScore >= 70 ? 'Good' : 'Needs Improvement'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quiz Management - Show on Dashboard and Quizzes pages */}
      {(showDashboard || showQuizzesOnly) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Quiz Management</CardTitle>
                <CardDescription>Upload and manage your quizzes</CardDescription>
              </div>
              <div className="flex gap-2">
                <Dialog open={uploadQuizOpen} onOpenChange={setUploadQuizOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Quiz
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Upload Quiz JSON</DialogTitle>
                      <DialogDescription>
                        Upload quiz files or paste JSON content. Questions and answers should be in the same JSON file.
                        <a 
                          href="/quiz-format.html" 
                          target="_blank" 
                          className="text-blue-600 hover:underline ml-1"
                        >
                          View format documentation
                        </a>
                      </DialogDescription>
                    </DialogHeader>
                    
                    {/* Mode Selector */}
                    <div className="flex gap-2 border-b">
                      <button
                        type="button"
                        onClick={() => setUploadMode('file')}
                        className={`px-4 py-2 border-b-2 transition-colors ${
                          uploadMode === 'file' 
                            ? 'border-blue-600 text-blue-600' 
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <FileJson className="w-4 h-4 inline mr-2" />
                        Upload Files
                      </button>
                      <button
                        type="button"
                        onClick={() => setUploadMode('text')}
                        className={`px-4 py-2 border-b-2 transition-colors ${
                          uploadMode === 'text' 
                            ? 'border-blue-600 text-blue-600' 
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <FileText className="w-4 h-4 inline mr-2" />
                        Paste JSON
                      </button>
                    </div>

                    <form onSubmit={handleUploadQuiz} className="space-y-4">
                      {uploadMode === 'file' ? (
                        <div className="space-y-2">
                          <Label htmlFor="quizFiles">Select JSON Files (Multiple supported)</Label>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                            <input
                              type="file"
                              id="quizFiles"
                              accept=".json"
                              multiple
                              onChange={handleFileChange}
                              className="w-full cursor-pointer"
                            />
                          </div>
                          {selectedFiles && selectedFiles.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <p className="text-sm text-gray-600">{selectedFiles.length} file(s) selected:</p>
                              {Array.from(selectedFiles).map((file, index) => (
                                <div key={index} className="text-xs text-gray-500 flex items-center">
                                  <FileJson className="w-3 h-3 mr-1" />
                                  {file.name}
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-gray-500">
                            Select one or more JSON quiz files. Each file should contain a single quiz.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label htmlFor="quizJson">Quiz JSON</Label>
                          <Textarea
                            id="quizJson"
                            value={quizJson}
                            onChange={(e) => setQuizJson(e.target.value)}
                            placeholder={JSON.stringify(sampleQuizJson, null, 2)}
                            className="font-mono text-sm min-h-[300px]"
                            required={uploadMode === 'text'}
                          />
                          <p className="text-xs text-gray-500">
                            See placeholder for example format. Must include: title, courseId, questions array
                          </p>
                        </div>
                      )}
                      <Button type="submit" className="w-full">
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadMode === 'file' 
                          ? `Upload ${selectedFiles ? selectedFiles.length : 0} File(s)` 
                          : 'Upload Quiz'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {quizzes.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No quizzes uploaded yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quiz Title</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead>Time Limit</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quizzes.map((quiz) => {
                    const course = courses.find(c => c.id === quiz.courseId);
                    return (
                      <TableRow key={quiz.id}>
                        <TableCell>{quiz.title}</TableCell>
                        <TableCell>{course?.name || 'Unknown'}</TableCell>
                        <TableCell>{quiz.questions?.length || 0}</TableCell>
                        <TableCell>{quiz.timeLimit} min</TableCell>
                        <TableCell>{new Date(quiz.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedQuiz(quiz);
                              setQuizDetailOpen(true);
                              setExpandedQuestions(new Set());
                            }}
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuiz(quiz.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quiz Detail Dialog */}
      <Dialog open={quizDetailOpen} onOpenChange={setQuizDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{selectedQuiz?.title}</DialogTitle>
            <DialogDescription>
              {selectedQuiz?.description || 'No description'} • {selectedQuiz?.questions?.length || 0} questions • {selectedQuiz?.timeLimit} minutes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedQuiz?.questions?.map((question, index) => {
              const isExpanded = expandedQuestions.has(question.id);
              return (
                <Card key={question.id} className="border">
                  <CardHeader 
                    className="cursor-pointer hover:bg-gray-50 transition-colors py-3"
                    onClick={() => {
                      const newExpanded = new Set(expandedQuestions);
                      if (isExpanded) {
                        newExpanded.delete(question.id);
                      } else {
                        newExpanded.add(question.id);
                      }
                      setExpandedQuestions(newExpanded);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            Q{index + 1}
                          </Badge>
                          <Badge 
                            variant="secondary" 
                            className={question.type === 'mcq' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}
                          >
                            {question.type === 'mcq' ? 'Multiple Choice' : 'Short Answer'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {question.points} pts
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                          <MathJax>{question.question}</MathJax>
                        </p>
                      </div>
                      <div className="ml-2">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent className="pt-0 pb-4">
                      {/* Options for MCQ */}
                      {question.type === 'mcq' && question.options && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-gray-500 mb-2">OPTIONS:</p>
                          <div className="space-y-1">
                            {question.options.map((option: string, optIndex: number) => {
                              const isCorrect = question.correctAnswer?.answer === option || question.correctAnswer === option;
                              return (
                                <div 
                                  key={optIndex} 
                                  className={`p-2 rounded text-sm ${
                                    isCorrect 
                                      ? 'bg-green-50 border border-green-200 text-green-800' 
                                      : 'bg-gray-50 border border-gray-200'
                                  }`}
                                >
                                  <span className="font-medium mr-2">{String.fromCharCode(65 + optIndex)}.</span>
                                  <MathJax inline>{option}</MathJax>
                                  {isCorrect && <span className="ml-2 text-green-600 font-medium">✓ Correct</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Correct Answer */}
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">CORRECT ANSWER:</p>
                        <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                          <MathJax>
                            {typeof question.correctAnswer === 'object' 
                              ? question.correctAnswer.answer 
                              : question.correctAnswer}
                          </MathJax>
                        </div>
                      </div>
                      
                      {/* Explanation */}
                      {(question.correctAnswer?.explanation || question.explanation) && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">EXPLANATION:</p>
                          <div className="p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 whitespace-pre-wrap">
                            <MathJax>{question.correctAnswer?.explanation || question.explanation}</MathJax>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
          
          <div className="flex justify-between items-center mt-4 pt-4 border-t">
            <div className="text-sm text-gray-500">
              Total: {selectedQuiz?.questions?.reduce((sum, q) => sum + (q.points || 0), 0)} points
            </div>
            <Button variant="outline" onClick={() => setQuizDetailOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}